/**
 * Why a business unit's industry packs are what they are - one component for
 * the Business Units page and the Classification engine page, so both say the
 * same thing in the same words.
 *
 * A unit's packs come from two places, and an operator needs to tell them
 * apart: a pack derived from the unit's industry in the CONSENT console (Core),
 * and a pack an administrator added HERE in Discovery. The first changes when
 * the unit's industry changes in the consent console; the second is Discovery's
 * own and consent never switches it off.
 */
import { Badge } from '@/components/ui/badge';

export type PackOrigin = 'consent' | 'discovery';

export interface ProvenancePack {
  packId: string;
  anchor: string;
  name?: string;
  origins: ReadonlyArray<PackOrigin>;
  fromIds: ReadonlyArray<string>;
  families?: ReadonlyArray<string>;
}

/** `overlay-clinical-trial-cdsco` -> "Clinical trial cdsco"; `bfsi` -> "BFSI". */
export function humanisePackId(id: string): string {
  const acronyms = new Set(['bfsi', 'nbfc', 'rbi', 'sebi', 'cdsco', 'dpdpa', 'uidai', 'abdm', 'cert', 'amc', 'ria', 'upi', 'kyc', 'fema', 'fiu', 'str', 'dgft', 'stpi', 'sez', 'fcra', 'ofac', 'cii', 'cea', 'mfi', 'hfc', 'aa', 'it', 'saas', 'psu', 'p2p', 'nabl', 'ctri', 'abha', 'uhid', 'ckyc']);
  const words = id
    .replace(/^overlay-/, '')
    .replace(/^bundle-/, '')
    .split(/[-_]+/)
    .filter(Boolean)
    .map((w) => (acronyms.has(w) ? w.toUpperCase() : w));
  if (words.length === 0) return id;
  const first = words[0]!;
  words[0] = first === first.toUpperCase() ? first : first.charAt(0).toUpperCase() + first.slice(1);
  return words.join(' ');
}

function originLabel(origins: ReadonlyArray<PackOrigin>): string {
  if (origins.includes('consent') && origins.includes('discovery')) return 'from consent + added here';
  if (origins.includes('consent')) return 'from consent';
  return 'added in Discovery';
}

/** Pack chips, each saying where it came from. */
export function PackOriginChips({ packs }: { packs: ReadonlyArray<ProvenancePack> }) {
  return (
    <>
      {packs.map((p) => {
        const fromConsent = p.origins.includes('consent');
        const via = p.fromIds.map(humanisePackId).join(', ');
        return (
          <Badge
            key={p.packId}
            variant={fromConsent ? 'info' : 'glass'}
            className="text-[10px] gap-1"
            title={`${p.packId}${via ? ` - on because of: ${p.fromIds.join(', ')}` : ''}${p.families?.length ? ` (family ${p.families.join(', ')})` : ''}`}
          >
            <span>{p.name ?? p.anchor}</span>
            <span className="opacity-70">· {originLabel(p.origins)}</span>
          </Badge>
        );
      })}
    </>
  );
}

/** Regulatory bundles: shown for what they are, never as "contributes nothing". */
export function BundleChips({ bundles }: { bundles: ReadonlyArray<string> }) {
  if (bundles.length === 0) return null;
  return (
    <>
      {bundles.map((b) => (
        <Badge key={b} variant="glass" className="text-[10px]" title={`Regulatory bundle ${b} - obligations, no detectors by design`}>
          {humanisePackId(b)} · regulatory
        </Badge>
      ))}
    </>
  );
}

/** One line of prose saying why the unit has the packs it has. */
export function resolutionSentence(args: {
  mode: 'override' | 'inherited' | 'tenant_default';
  inheritedFromName?: string | null;
  fallThroughReason?: 'unit_packs_carry_no_classifiers' | null;
  packCount: number;
}): string {
  const { mode, inheritedFromName, fallThroughReason, packCount } = args;
  const lead =
    fallThroughReason === 'unit_packs_carry_no_classifiers'
      ? "This unit's own packs carry no detectors (a regulatory bundle, or an unmapped id), so "
      : '';
  if (packCount === 0) return `${lead}No industry pack is in force: Tier 1 detectors only.`;
  if (mode === 'override') return `${lead}Packs set on this unit.`;
  if (mode === 'inherited') {
    return `${lead}${lead ? 'it' : 'This unit'} inherits the packs of ${inheritedFromName ?? 'its parent unit'}.`;
  }
  return `${lead}${lead ? 'the' : 'The'} tenant default packs apply.`;
}
